import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { DESIGNATION_QUERY_KEYS } from './keys';
import { designationsQueryOptions, getDesignationsQueryKey } from './useLookupQueries';

import * as designationService from 'services/designation';

import { handleError } from 'utils/errorHandler';

import type { Designation } from 'interface/designation';
import type { ServiceId } from 'interface/service';

export const getDesignationByIdQueryKey = (id?: ServiceId) => [DESIGNATION_QUERY_KEYS.DETAIL, id] as const;

export const useFetchDesignationByIdQuery = <TData = unknown>(
  id?: ServiceId,
  enabled = true
): UseQueryResult<TData, Error> => {
  return useQuery({
    queryKey: getDesignationByIdQueryKey(id),
    queryFn: () => designationService.fetchById(id!) as Promise<TData>,
    enabled: Boolean(id) && enabled,
  });
};

const useInvalidateDesignations = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: [DESIGNATION_QUERY_KEYS.ALL] });
    queryClient.invalidateQueries({ queryKey: getDesignationsQueryKey() });
  };
};

const handleMutationError = (error: object) => handleError(error);

export const useCreateDesignationMutation = () => {
  const invalidateDesignations = useInvalidateDesignations();

  return useMutation({
    mutationFn: (designation: Designation) => designationService.create(designation),
    onSuccess: invalidateDesignations,
    onError: handleMutationError,
  });
};

export const useUpdateDesignationEntityMutation = () => {
  const invalidateDesignations = useInvalidateDesignations();

  return useMutation({
    mutationFn: ({ designation, id }: { designation: Designation; id: ServiceId }) =>
      designationService.update(designation, id),
    onSuccess: invalidateDesignations,
    onError: handleMutationError,
  });
};

export const useDeleteDesignationMutation = () => {
  const invalidateDesignations = useInvalidateDesignations();

  return useMutation({
    mutationFn: (id: ServiceId) => designationService.remove(id),
    onSuccess: invalidateDesignations,
    onError: handleMutationError,
  });
};

export const useDesignationsListQuery = (enabled = true): UseQueryResult<Designation[], Error> => {
  return useQuery({
    ...designationsQueryOptions(),
    select: (response) => response.data ?? [],
    enabled,
  });
};
